'use client';
import {
  USCodeToState,
  UserRole,
  isOnFullScreenPath,
} from '@baseball-near-you/common';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Form,
  FormInput,
  Separator,
} from '@baseball-near-you/ui';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useUpdateUserMutation } from '@bb/lib/mutations/useUpdateUserMutation';
import { z } from 'zod';

import { dayjsExt } from '@bb/lib/dayjs';
import { usePathname } from 'next/navigation';

const MAX_AGE = 10;
const MAX_BIRTHDAY = dayjsExt().subtract(MAX_AGE, 'year').toISOString();

export function PersonalUserGeneralForm(props: {
  onSuccess?: () => Promise<void>;
  submitButtonLabel?: string;
}) {
  const { userProfile } = useUserProfile();
  const { updateUser, updatingUser, userError } = useUpdateUserMutation();

  return userProfile ? (
    <Form
      schema={z.object({
        ...(userProfile.role === UserRole.Parent && {
          parentFirstName: z.string().min(1, {
            message: 'Your first name is required.',
          }),
          parentLastName: z.string().min(1, {
            message: 'Your last name is required.',
          }),
        }),
        firstName: z.string().min(1, {
          message:
            userProfile.role === UserRole.Parent
              ? "Your athlete's first name is required."
              : 'First name is required.',
        }),
        lastName: z.string().min(1, {
          message:
            userProfile.role === UserRole.Parent
              ? "Your athlete's last name is required."
              : 'Last name is required.',
        }),
        ...((userProfile.role === UserRole.Athlete ||
          userProfile.role === UserRole.Parent) && {
          birthday: z
            .string()
            .superRefine((value, ctx) => {
              const date = dayjsExt(value);
              const maxBirthday = dayjsExt(MAX_BIRTHDAY);
              if (!date.isValid()) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Birthday is invalid.',
                });
              }
              console.log(
                '\n\ndate\n\n',
                date.toDate(),
                '\n\nvalue\n\n',
                value,
                MAX_BIRTHDAY,
                '\n\nmaxBirthday\n\n',
                maxBirthday.toDate(),
                '\n\nisAfter\n\n',
                date.isAfter(maxBirthday)
              );
              if (date.isAfter(maxBirthday)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: `Your athlete must be at least ${MAX_AGE} years old.`,
                });
              }
              return date.isBefore(maxBirthday);
            })
            .optional()
            .or(z.literal('')),
          graduationYear: z
            .unknown()
            .superRefine((value, ctx) => {
              console.log('value', typeof value);
              const year = Number(value);
              if (Number.isNaN(year)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Graduation year is invalid.',
                });
              }
            })
            .optional()
            .or(z.literal('')),
          schoolName: z
            .string()
            .min(1, {
              message: 'School name is required.',
            })
            .optional()
            .or(z.literal('')),
          state: z
            .string()
            .max(2, {
              message: 'State abbreviation is required.',
            })
            .min(2, {
              message: 'State abbreviation is required.',
            })
            .refine(
              (value) =>
                Object.keys(USCodeToState).includes(value.toUpperCase()),
              {
                message: 'State is invalid.',
              }
            )
            .optional()
            .or(z.literal('')),
        }),
      })}
      initialValues={{
        firstName: userProfile?.firstName ?? '',
        lastName: userProfile?.lastName ?? '',
        ...(userProfile.role === UserRole.Parent && {
          parentFirstName: userProfile?.parentFirstName ?? '',
          parentLastName: userProfile?.parentLastName ?? '',
        }),
        ...((userProfile.role === UserRole.Athlete ||
          userProfile.role === UserRole.Parent) && {
          birthday: userProfile.birthday
            ? dayjsExt(userProfile.birthday).format('YYYY-MM-DD')
            : undefined,
          graduationYear: userProfile.graduationYear
            ? userProfile.graduationYear
            : undefined,
          schoolName: userProfile.schoolName ?? '',
          state: userProfile.state ?? '',
        }),
      }}
      onSubmit={async ({ firstName, lastName, ...rest }) => {
        if (!userProfile) return;
        await updateUser({
          id: userProfile.id,
          firstName,
          lastName,
          birthday: 'birthday' in rest ? new Date(rest.birthday as any) : null,
          graduationYear:
            'graduationYear' in rest ? Number(rest.graduationYear) : null,
          schoolName: 'schoolName' in rest ? (rest.schoolName as string) : null,
          state: 'state' in rest ? (rest.state as string).toUpperCase() : null,
          parentFirstName:
            'parentFirstName' in rest ? (rest.parentFirstName as string) : null,
          parentLastName:
            'parentLastName' in rest ? (rest.parentLastName as string) : null,
        });
        await props.onSuccess?.();
      }}
      className="flex flex-col gap-y-5"
    >
      {userProfile.role === UserRole.Parent && (
        <>
          <FormInput
            name="parentFirstName"
            label="Your first name"
            placeholder="John"
          />
          <FormInput
            name="parentLastName"
            label="Your last name"
            placeholder="Doe"
          />
          <div className="flex w-full flex-col items-start justify-center">
            <Separator className="h-[0.07rem] w-full bg-gray-200" />
            {userProfile.role === UserRole.Parent && (
              <p className="-mb-2.5 mt-4 text-gray-700 text-lg">
                Details about your athlete
              </p>
            )}
          </div>
        </>
      )}
      <FormInput name="firstName" label="First Name" placeholder="John" />
      <FormInput name="lastName" label="Last Name" placeholder="Doe" />
      {(userProfile.role === UserRole.Athlete ||
        userProfile.role === UserRole.Parent) && <AthleteNameForm />}
      <Button
        className="mt-3 w-full bg-red transition-all hover:bg-red/80 md:hover:scale-105"
        loading={updatingUser}
        type="submit"
      >
        {props.submitButtonLabel ?? 'Update'}
      </Button>
    </Form>
  ) : null;
}

export function AthleteNameForm() {
  const { userProfile } = useUserProfile();
  const pathname = usePathname();
  const isOnFullScreen = isOnFullScreenPath(pathname);
  return (
    <Accordion
      type="single"
      collapsible
      value={isOnFullScreen ? undefined : 'additional-information'}
    >
      <AccordionItem value="additional-information">
        <AccordionTrigger className="font-medium text-gray-600">
          <p>
            Additional Information{' '}
            <span className="ml-2 rounded-sm bg-gray-200 px-1.5 py-0.5 text-gray-500 text-xs">
              optional
            </span>
          </p>
        </AccordionTrigger>
        <AccordionContent
          className="flex flex-col gap-y-4"
          style={{
            // to have the border on an active input not be cut off
            margin: '0 -5px',
            padding: '0 5px',
          }}
        >
          <FormInput
            type="date"
            name="birthday"
            label="Birthday (MM/DD/YYYY)"
            className="block"
            max={MAX_BIRTHDAY}
            subtitle={`This is how we'll match ${
              userProfile?.role === UserRole.Parent ? 'your athlete' : 'you'
            } with the right age group.`}
          />
          <FormInput
            name="graduationYear"
            label="Graduation Year"
            placeholder={new Date().getFullYear().toString()}
            type="number"
          />
          <FormInput
            name="schoolName"
            label="School Name"
            placeholder="Cooperstown"
          />
          <FormInput
            name="state"
            label="State"
            placeholder="NY"
            maxLength={2}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
